/**
 * Navigation Icon PNG Assets
 *
 * PNG icon images for navigation and hero sections.
 * Used primarily in mobile apps where PNG icons are preferred.
 */

import type { AssetPath, HeroType } from '../types';

/**
 * Base path for icon assets
 */
const ICONS_BASE = '/assets/icons/heroes';

/**
 * Navigation/Feature icon type (extends HeroType with additional features)
 */
export type NavPngIconType = HeroType | 'home' | 'billing' | 'career' | 'family' | 'profile';

/**
 * PNG navigation icon assets
 */
export const NAV_PNG_ICONS: Record<NavPngIconType, AssetPath> = {
  // Hero icons
  checkup: {
    path: `${ICONS_BASE}/checkup.png`,
    alt: 'CheckUp icon',
    width: 64,
    height: 64,
  },
  teleportal: {
    path: `${ICONS_BASE}/teleportal.png`,
    alt: 'TelePortal icon',
    width: 64,
    height: 64,
  },
  qultureup: {
    path: `${ICONS_BASE}/qultureup.png`,
    alt: 'QultureUp icon',
    width: 64,
    height: 64,
  },
  care: {
    path: `${ICONS_BASE}/care.png`,
    alt: 'Care icon',
    width: 64,
    height: 64,
  },
  passport: {
    path: `${ICONS_BASE}/passport.png`,
    alt: 'Passport icon',
    width: 64,
    height: 64,
  },

  // Feature icons
  home: {
    path: `${ICONS_BASE}/home.png`,
    alt: 'Home icon',
    width: 64,
    height: 64,
  },
  billing: {
    path: `${ICONS_BASE}/billing.png`,
    alt: 'Billing icon',
    width: 64,
    height: 64,
  },
  career: {
    path: `${ICONS_BASE}/career.png`,
    alt: 'Career icon',
    width: 64,
    height: 64,
  },
  family: {
    path: `${ICONS_BASE}/family.png`,
    alt: 'Family icon',
    width: 64,
    height: 64,
  },
  profile: {
    path: `${ICONS_BASE}/profile.png`,
    alt: 'Profile icon',
    width: 64,
    height: 64,
  },
} as const;

/**
 * PNG navigation icon paths (simple string access)
 */
export const NAV_PNG_ICON_PATHS: Record<NavPngIconType, string> = Object.entries(
  NAV_PNG_ICONS
).reduce(
  (acc, [key, value]) => {
    acc[key as NavPngIconType] = value.path;
    return acc;
  },
  {} as Record<NavPngIconType, string>
);

/**
 * Get PNG navigation icon path
 */
export function getNavPngIcon(type: NavPngIconType): string {
  return NAV_PNG_ICONS[type].path;
}

/**
 * Get PNG navigation icon asset with metadata
 */
export function getNavPngIconAsset(type: NavPngIconType): AssetPath {
  return NAV_PNG_ICONS[type];
}
